/**
 * CASL field-level permission filtering.
 *
 * Utilities for filtering entity data based on CASL abilities,
 * supporting both read and write field-level permissions.
 */

import { permittedFieldsOf } from '@casl/ability/extra'
import type { MongoAbility, RawRuleOf } from '@casl/ability'
import type { PaginatedResult, ModuleContext } from '@gzl10/nexus-sdk'

/**
 * Filter entity data to only include permitted fields based on CASL ability.
 * If no field restrictions exist, returns all fields.
 */
export function filterByPermittedFields<T extends Record<string, unknown>>(
  data: T,
  ability: unknown,
  action: 'read' | 'update',
  subject: string,
  allFields: string[]
): Partial<T> {
  if (!ability) return data

  // Get permitted fields from CASL ability
  const permitted = permittedFieldsOf(ability as MongoAbility, action, subject, {
    fieldsFrom: (rule) => rule.fields || allFields
  })

  // If no restrictions (empty array with fieldsFrom means all allowed)
  if (permitted.length === 0 || permitted.length === allFields.length) {
    return data
  }

  // Filter to only permitted fields
  const result: Partial<T> = {}
  for (const field of permitted) {
    if (field in data) {
      result[field as keyof T] = data[field as keyof T]
    }
  }
  // Always include id if present
  if ('id' in data && !('id' in result)) {
    result['id' as keyof T] = data['id' as keyof T]
  }
  return result
}

/**
 * Filter paginated result by permitted fields.
 */
export function filterResultByPermittedFields<T extends Record<string, unknown>>(
  result: PaginatedResult<T>,
  ability: unknown,
  subject: string,
  allFields: string[]
): PaginatedResult<Partial<T>> {
  if (!ability) return result
  return {
    ...result,
    items: result.items.map(item => filterByPermittedFields(item, ability, 'read', subject, allFields))
  }
}

/**
 * Validate that user can write all fields in payload.
 * @throws ForbiddenError if user tries to write restricted fields
 */
export function validateWritePermissions(
  payload: Record<string, unknown>,
  ability: unknown,
  action: 'create' | 'update',
  subject: string,
  allFields: string[],
  ctx: ModuleContext
): void {
  if (!ability) return

  const permitted = permittedFieldsOf(ability as MongoAbility, action, subject, {
    fieldsFrom: (rule) => rule.fields || allFields
  })

  // If no restrictions, all fields are allowed
  if (permitted.length === 0 || permitted.length === allFields.length) {
    return
  }

  // Check each field in payload
  const forbiddenFields = Object.keys(payload).filter(
    field => !permitted.includes(field) && field !== 'id'
  )

  if (forbiddenFields.length > 0) {
    throw new ctx.core.errors.ForbiddenError(
      `Not allowed to modify fields: ${forbiddenFields.join(', ')}`
    )
  }
}

/**
 * Extract CASL conditions to apply as query filters (row-level security).
 *
 * Analyzes the ability rules for the given action/subject and returns
 * conditions that should be added as WHERE clauses to the query.
 *
 * Returns null when no filtering is needed (user has unconditional access
 * or no matching rules exist).
 */
export function getCaslConditionsForQuery(
  ability: unknown,
  action: string,
  subject: string
): Record<string, unknown> | null {
  const mongoAbility = ability as MongoAbility | null
  if (!mongoAbility?.rules) return null

  const rules = mongoAbility.rules as RawRuleOf<MongoAbility>[]

  // Filter rules that allow this action on this subject
  const matchingRules = rules.filter(rule => {
    if (rule.inverted) return false

    const actions = Array.isArray(rule.action) ? rule.action : [rule.action]
    if (!actions.includes(action) && !actions.includes('manage')) return false

    const subjects = Array.isArray(rule.subject) ? rule.subject : [rule.subject]
    if (!subjects.includes(subject) && !subjects.includes('all')) return false

    return true
  })

  if (matchingRules.length === 0) return null

  // If any matching rule has NO conditions, user has full access (no filter)
  if (matchingRules.some(rule => !rule.conditions)) return null

  // All matching rules have conditions — CASL rules are additive (OR between rules)
  const conditionGroups = matchingRules
    .filter(rule => rule.conditions)
    .map(rule => rule.conditions as Record<string, unknown>)

  if (conditionGroups.length === 0) return null

  // Single rule: use conditions directly
  if (conditionGroups.length === 1) return conditionGroups[0]!

  // Check if all conditions share the same single key (optimization: use $in)
  const allKeys = conditionGroups.map(c => Object.keys(c))
  const firstKey = allKeys[0]?.[0]
  const sameKey = firstKey != null && allKeys.every(k => k.length === 1 && k[0] === firstKey)

  if (sameKey) {
    const values = [...new Set(conditionGroups.map(c => c[firstKey]))]
    return values.length === 1 ? { [firstKey]: values[0] } : { [firstKey]: values }
  }

  // Different keys across rules: use $or (supported by applyFilters)
  return { $or: conditionGroups }
}
